<template>
  <UForm
    ref="form"
    v-bind="{ ...props, ...$attrs }"
    :schema="schema"
    class="p-form"
    @error="emit('error', $event)"
  >
    <template v-if="slots.default" #default="slotProps">
      <slot v-bind="slotProps" />
    </template>
  </UForm>
</template>

<script
  setup
  lang="ts"
  generic="S extends FormSchema = FormSchema, T extends boolean = true, N extends boolean = false"
>
import { computed, useTemplateRef } from "vue"
import NuxtForm, {
  type FormProps,
  type FormEmits,
  type FormSlots
} from "@nuxt/ui/components/Form.vue"
import type { FormSchema } from "@nuxt/ui"
import type { ComponentExposed } from "vue-component-type-helpers"

const UForm = NuxtForm<S, T, N>
const props = withDefaults(defineProps<FormProps<S, T, N>>(), {
  disabled: undefined,
  loadingAuto: undefined,
  transform: undefined,
  nested: undefined
})
const emit = defineEmits<FormEmits<S, T>>()
const slots = defineSlots<FormSlots>()
const form = useTemplateRef<ComponentExposed<typeof UForm>>("form")

const schema = computed(() => {
  const schema = props.schema
  if (!schema || !("~standard" in schema)) return schema
  const standard = schema["~standard"]
  return {
    ...schema,
    "~standard": {
      ...standard,
      async validate(value: unknown) {
        const result = await standard.validate(value)
        // Regle returns issues: [] on success; UForm otherwise discards the validated output.
        if (!result.issues?.length && "value" in result) return { value: result.value }
        return result
      }
    }
  }
})

// Forward onSubmit as a prop so UForm can await the handler and retain loading/error behavior.
defineOptions({ inheritAttrs: false })
defineExpose({
  get validate() {
    return form.value!.validate
  },
  get submit() {
    return form.value!.submit
  },
  get setErrors() {
    return form.value!.setErrors
  },
  get getErrors() {
    return form.value!.getErrors
  },
  get clear() {
    return form.value!.clear
  },
  get errors() {
    return form.value!.errors
  },
  get disabled() {
    return form.value!.disabled
  },
  get loading() {
    return form.value!.loading
  },
  get dirty() {
    return form.value!.dirty
  },
  get dirtyFields() {
    return form.value!.dirtyFields
  },
  get touchedFields() {
    return form.value!.touchedFields
  },
  get blurredFields() {
    return form.value!.blurredFields
  }
})
</script>
